/**
 * L10 — Multi-image Grid
 * 2–4 images arranged in a grid with headline overlay.
 * Adapts layout based on image count.
 */
import React from 'react';
import { AbsoluteFill, Img } from 'remotion';
import type { SceneProps } from '../../types';
import type { UploadedAsset } from '../../../types';
import {
  SceneContainer, HeadlineText, GlobalOverlays,
  AccentBar, useSceneOpacity,
} from './shared';

const GridCell: React.FC<{ asset?: UploadedAsset; style?: React.CSSProperties }> = ({ asset, style }) => {
  const opacity = useSceneOpacity();
  if (!asset) {
    return <div style={{ background: '#1e293b', borderRadius: 8, ...style }} />;
  }
  return (
    <div style={{ overflow: 'hidden', borderRadius: 8, ...style }}>
      <Img
        src={asset.url}
        style={{ width: '100%', height: '100%', objectFit: 'cover', opacity }}
      />
    </div>
  );
};

export const L10_MultiImageGrid: React.FC<SceneProps> = (props) => {
  const { headline, assets, colorPalette, headlineFont } = props;

  // Collect all images
  const images: UploadedAsset[] = [];
  if (assets.primary) images.push(assets.primary);
  if (assets.secondary) images.push(assets.secondary);
  if (assets.images) images.push(...assets.images.slice(0, 2));
  const count = Math.min(images.length, 4) || 2;

  return (
    <SceneContainer bg={colorPalette.background}>
      {/* Grid */}
      <AbsoluteFill style={{ padding: '60px 60px 130px', boxSizing: 'border-box' }}>
        <div style={{
          display: 'grid',
          gridTemplateColumns: count <= 2 ? '1fr 1fr' : '1fr 1fr',
          gridTemplateRows: count <= 2 ? '1fr' : '1fr 1fr',
          gap: 8, width: '100%', height: '100%',
        }}>
          {Array.from({ length: count }).map((_, i) => (
            <GridCell key={i} asset={images[i]} />
          ))}
        </div>
      </AbsoluteFill>

      {/* Bottom headline */}
      <AbsoluteFill style={{
        background: 'linear-gradient(to top, rgba(0,0,0,0.9) 0%, transparent 40%)',
      }} />
      <AbsoluteFill style={{
        display: 'flex', flexDirection: 'column',
        justifyContent: 'flex-end', padding: '0 80px 100px',
      }}>
        <AccentBar color={colorPalette.accent} style={{ marginBottom: 12 }} />
        <HeadlineText
          text={headline}
          font={{ ...headlineFont, color: '#fff', fontSize: Math.min(headlineFont.fontSize * 0.85, 40) }}
          delay={10}
          maxLines={2}
          layerKey="headline"
        />
      </AbsoluteFill>

      <GlobalOverlays props={props} />
    </SceneContainer>
  );
};
